/**
 * Telling this app's other windows that something changed.
 *
 * Two windows on one game (a phone with the book open in two tabs, a laptop on
 * the scorer's table with a second window) each held their own copy of the
 * log in memory and appended only their own plays, while Undo read the shared
 * disk. So the screens disagreed and Undo could void the other window's play
 * (issue #45). A message after each write lets the other window re-read.
 *
 * BroadcastChannel on the web. A Capacitor build runs in one webview, which
 * has BroadcastChannel, so this reports supported there too and a post simply
 * reaches no other window. That is harmless and needs no native swap: the
 * nudge only ever asks another window to re-read, and with none there is
 * nothing to keep in step. Only a host without BroadcastChannel reports
 * unsupported, and then every call is a no-op.
 *
 * ONE channel object per name, shared by `post` and `subscribe`. A channel
 * object never receives its own messages, so a window is not told about its
 * own write; two objects in one window would each hear the other.
 */

import { browserWindow, type Adapter } from './host.js';

export interface Broadcast extends Adapter {
  /** Tell every OTHER window of this app listening on `channel`. Never throws. */
  readonly post: (channel: string, message: string) => void;
  /** Hear the other windows. Returns an unsubscribe function. */
  readonly subscribe: (channel: string, listener: (message: string) => void) => () => void;
}

const open = new Map<string, BroadcastChannel>();

function channelFor(name: string): BroadcastChannel | null {
  const Channel = browserWindow()?.BroadcastChannel;
  if (typeof Channel !== 'function') return null;
  let channel = open.get(name);
  if (channel === undefined) {
    channel = new Channel(name);
    open.set(name, channel);
  }
  return channel;
}

export const broadcast: Broadcast = {
  isSupported: () => typeof browserWindow()?.BroadcastChannel === 'function',

  post: (name, message) => {
    try {
      channelFor(name)?.postMessage(message);
    } catch {
      // A closed channel. The other window's screen stays stale until its
      // next play or swap (which reads the disk first), its next re-read, or
      // a reopen; a missed nudge is a stale screen, never a lost play.
    }
  },

  subscribe: (name, listener) => {
    const channel = channelFor(name);
    if (channel === null) return () => {};
    const handler = (event: MessageEvent) => {
      if (typeof event.data === 'string') listener(event.data);
    };
    channel.addEventListener('message', handler);
    return () => {
      channel.removeEventListener('message', handler);
    };
  },
};
